import React from 'react';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  paleChipSx,
  severityFromInterpretation
} from 'src/components/UI/chips/paleChipSx';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import { TILE_COLOURS } from '../../tileColours';
import {
  GENOME_BUILDS,
  isReportVariant,
  isVersionedTranscript,
  statedValueText
} from './test-record/testRecordModel';
import FromReportMark from './test-record/FromReportMark';
import UnsavedChangeNote from 'src/components/UI/input/UnsavedChangeNote';
import { unsavedTileSx } from 'src/components/UI/input/unsavedChange';
import AnchorMark from 'src/components/lab-report-verification/AnchorMark';
import {
  highlightSx,
  useVerification
} from 'src/components/lab-report-verification/useVerificationView';
import { variantTarget } from 'src/components/lab-report-verification/verificationTargets';
import { variantInterpretationText } from './formService';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';

const HGVS_FIELDS = [
  { key: 'variantG', prefix: 'g.' },
  { key: 'variantC', prefix: 'c.' },
  { key: 'variantP', prefix: 'p.' }
];

const ORIGIN_LABELS = { germline: 'Germline', somatic: 'Somatic' };

const renderHgvs = (variant) => {
  const parts = HGVS_FIELDS.map(({ key, prefix }) => {
    const raw = variant[key];
    if (!raw) return null;
    return raw.startsWith(prefix) ? raw : `${prefix}${raw}`;
  }).filter(Boolean);

  if (parts.length === 0)
    return variant.gene || translate('Unspecified variant');
  return parts.join(' · ');
};

const CAPTION_SX = {
  display: 'block',
  color: 'text.secondary',
  mt: 0.25,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
};

// What a variant from a lab report still needs before the Test can be saved (the save refuses it
// otherwise: backend services/testResults/testRecord.js). Said on the tile, where it is fixed, in
// one sentence for each case.
function reportGapsText(variant, t) {
  if (!isReportVariant(variant)) return '';
  const build = !GENOME_BUILDS.includes(variant.genomeBuild);
  const transcript = !isVersionedTranscript(variant.transcript);
  if (build && transcript) {
    return t(
      'From a lab report: needs a genome build and a versioned transcript before the Test can be saved.'
    );
  }
  if (build) {
    return t(
      'From a lab report: needs a genome build before the Test can be saved.'
    );
  }
  if (transcript) {
    return t(
      'From a lab report: needs a versioned transcript before the Test can be saved.'
    );
  }
  return '';
}

// A Test's variant (TGCLOUD-1866, Phase 2 rework). The form's own fields first: the HGVS, and the
// clinician-facing interpretation ("Lab: Pathogenic", the five-tier value the genotype record
// reads). Then, when the variant carries them (a lab report's variant does), what the lab printed
// about it: its transcript and build, zygosity and inheritance, and the laboratory's
// classification in its own words with the framework and criteria it names. Those are the lab's
// words, shown verbatim, and are never edited here.
//
// While a lab report is checked beside the Test (TGCLOUD-1868, plan D3), a variant read from it
// (one with a findingId) says where the report prints it (AnchorMark), and is ringed while the
// report's selection is about it. Its reading's two groups, the variant and its laboratory
// classification, are confirmed on the tile itself (`checks`: VariantGroupCards).
const SavedVariantTile = ({
  variant,
  onDelete,
  onEdit,
  isEditing,
  disabled,
  fromReport = false,
  // A variant the Test as saved does not hold, while a lab report is applied (the report's or one
  // entered or edited by hand): drawn as the data entry grid draws an unsaved change
  // (unsavedChange.js). Its chip, while it has one, says the variant is still the report's.
  unsaved = false,
  // What is confirmed on the tile while its report is checked: drawn under the variant.
  checks = null
}) => {
  const t = useAutoTranslation();

  const fmtDate = useDateFormatter();
  const view = useVerification();
  const target = variant.findingId ? variantTarget(variant.findingId) : null;
  const highlighted =
    !!target && !!view && view.active && view.highlighted.has(target);
  const labSeverity = severityFromInterpretation(variant.variantInterpretation);
  const reference = [variant.transcript, variant.genomeBuild]
    .filter(Boolean)
    .join(' · ');
  // The lab's words as printed, else the value's label in the reader's language.
  const zygosity = statedValueText(variant.zygosity, t);
  const inheritance = statedValueText(variant.inheritance, t);
  const facts = [
    zygosity ? t('Zygosity: {{value}}', { value: zygosity }) : null,
    inheritance ? t('Inheritance: {{value}}', { value: inheritance }) : null,
    ORIGIN_LABELS[variant.origin]
      ? t('Origin: {{value}}', { value: t(ORIGIN_LABELS[variant.origin]) })
      : null
  ].filter(Boolean);
  const lc = variant.labClassification;
  const gaps = reportGapsText(variant, t);
  // The date last reviewed is the variant editor's picker value (a local instant), or UTC midnight
  // where an importer wrote it: read by how it was written (CLAUDE.md, Dates).
  const reviewed = variant.dateLastReviewed
    ? fmtDate(toLocalDateFromPickerOrImport(variant.dateLastReviewed))
    : '';

  return (
    <Box
      data-testid="saved-variant"
      data-verification-target={target || undefined}
      data-highlighted={highlighted ? 'true' : undefined}
      sx={{
        border: `1px solid ${TILE_COLOURS.genotype.border}`,
        borderLeft: `3px solid ${TILE_COLOURS.genotype.accent}`,
        backgroundColor: TILE_COLOURS.genotype.background,
        borderRadius: '6px',
        px: 1.5,
        py: 1,
        mb: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        opacity: isEditing ? 0.55 : 1,
        outline: isEditing
          ? `2px dashed ${TILE_COLOURS.genotype.accent}`
          : 'none',
        ...unsavedTileSx(unsaved),
        ...highlightSx(highlighted, { round: false })
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            flexWrap: 'wrap'
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: TILE_COLOURS.genotype.text,
              lineHeight: 1
            }}
          >
            {t('Variant')}
          </Typography>
          {variant.gene && (
            <Typography
              variant="caption"
              sx={{ color: TILE_COLOURS.genotype.subtext }}
            >
              {variant.gene}
            </Typography>
          )}
          {reference && (
            <Typography
              variant="caption"
              sx={{ color: TILE_COLOURS.genotype.subtext }}
            >
              {reference}
            </Typography>
          )}
          {fromReport && <FromReportMark />}
          {target && (
            <AnchorMark
              target={target}
              label={[
                variant.gene,
                variant.variantC || variant.variantG || variant.variantP
              ]
                .filter(Boolean)
                .join(' ')}
              sx={{ ml: 0 }}
            />
          )}
          {unsaved && <UnsavedChangeNote />}
        </Box>

        <Typography
          sx={{
            mt: 0.5,
            fontFamily: 'monospace',
            fontSize: 13,
            wordBreak: 'break-word'
          }}
        >
          {renderHgvs(variant)}
        </Typography>

        {facts.length > 0 && (
          <Typography variant="caption" sx={CAPTION_SX}>
            {facts.join(' · ')}
          </Typography>
        )}

        {lc?.asPrinted && (
          <Box sx={{ mt: 0.5 }} data-testid="lab-classification">
            <Typography variant="caption" sx={CAPTION_SX}>
              {t('Laboratory classification, as printed:')}{' '}
              <Box component="span" sx={{ color: 'text.primary' }}>
                {lc.asPrinted}
              </Box>
            </Typography>
            {lc.frameworkAsPrinted && (
              <Typography variant="caption" sx={CAPTION_SX}>
                {t('Framework:')} {lc.frameworkAsPrinted}
              </Typography>
            )}
            {(lc.criteria || []).length > 0 && (
              <Typography variant="caption" sx={CAPTION_SX}>
                {t('Criteria: {{criteria}}', {
                  criteria: lc.criteria.join(', ')
                })}
              </Typography>
            )}
          </Box>
        )}

        {reviewed && (
          <Typography variant="caption" sx={CAPTION_SX}>
            {t('Reviewed:')} {reviewed}
          </Typography>
        )}

        {variant.variantInterpretation && (
          <Box sx={{ mt: 1 }}>
            <Chip
              size="small"
              label={t('Lab: {{variantInterpretation}}', {
                variantInterpretation: variantInterpretationText(
                  variant.variantInterpretation,
                  t
                )
              })}
              sx={paleChipSx(labSeverity)}
            />
          </Box>
        )}

        {gaps && (
          <Typography
            variant="caption"
            sx={{ ...CAPTION_SX, color: 'warning.dark' }}
            data-testid="variant-needs"
          >
            {gaps}
          </Typography>
        )}
        {checks}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {onEdit && (
          <Tooltip title={isEditing ? t('Editing variant') : t('Edit variant')}>
            <span>
              <IconButton
                size="small"
                onClick={onEdit}
                disabled={disabled || isEditing}
                aria-label={t('Edit variant')}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title={t('Remove variant')}>
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={disabled}
                aria-label={t('Remove variant')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default SavedVariantTile;
